import React, { useState, useEffect } from 'react';

function Destination() {
  const [galleryItems, setGalleryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Grid configurations for different positions
  const gridConfig = {
    'large-left': {
      gridArea: 'large-left',
      height: '500px'
    },
    'large-right': {
      gridArea: 'large-right',
      height: '500px'
    },
    'medium-top': {
      gridArea: 'medium-top',
      height: '300px'
    },
    'medium-bottom': {
      gridArea: 'medium-bottom',
      height: '250px'
    },
    'small-1': {
      gridArea: 'small-1',
      height: '200px'
    },
    'small-2': {
      gridArea: 'small-2',
      height: '200px'
    }
  };

  // Grid template for desktop
  const desktopGridTemplate = `
    "large-left medium-top medium-top" 300px
    "large-left small-1 small-2" 200px
    "large-left medium-bottom medium-bottom" 250px
    "large-right large-right large-right" 500px /
    2fr 1fr 1fr
  `;

  // Grid template for tablet
  const tabletGridTemplate = `
    "large-left medium-top" 300px
    "large-left small-1" 200px
    "large-right small-2" 200px
    "large-right medium-bottom" 250px /
    1.5fr 1fr
  `;

  // Grid template for mobile
  const mobileGridTemplate = `
    "medium-top" 250px
    "small-1" 180px
    "small-2" 180px
    "medium-bottom" 200px
    "large-left" 300px
    "large-right" 300px /
    1fr
  `;

  useEffect(() => {
    fetchDestinationGallery();
  }, []);

  const fetchDestinationGallery = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://travel-agency-server-lilac.vercel.app/api/destination-gallery');

      if (!response.ok) {
        throw new Error('Failed to fetch destination gallery');
      }

      const data = await response.json();

      if (data.success) {
        setGalleryItems(data.galleryItems || []);
      }
    } catch (error) {
      console.error('Failed to fetch destination gallery:', error);
      setError('Failed to load destination gallery');
    } finally {
      setLoading(false);
    }
  };

  // Get items by position
  const getItemByPosition = (position) => {
    return galleryItems.find(item => item.position === position && item.isActive);
  };

  // Get grid style based on screen size
  const getGridStyle = () => {
    if (window.innerWidth >= 992) {
      return {
        display: 'grid',
        gridTemplate: desktopGridTemplate,
        gap: '15px',
        marginTop: '30px'
      };
    } else if (window.innerWidth >= 768) {
      return {
        display: 'grid',
        gridTemplate: tabletGridTemplate,
        gap: '15px',
        marginTop: '30px'
      };
    } else {
      return {
        display: 'grid',
        gridTemplate: mobileGridTemplate,
        gap: '15px',
        marginTop: '30px'
      };
    }
  };

  // Render gallery item
  const renderGalleryItem = (item, position) => {
    if (!item) return null;

    const config = gridConfig[position];

    return (
      <div
        key={position}
        style={{
          position: 'relative',
          overflow: 'hidden',
          borderRadius: '15px',
          boxShadow: '0 5px 15px rgba(0,0,0,0.1)',
          transition: 'all 0.4s ease',
          height: '100%',
          gridArea: config.gridArea
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-10px)';
          e.currentTarget.style.boxShadow = '0 15px 35px rgba(0,0,0,0.2)';
          const img = e.currentTarget.querySelector('img');
          if (img) img.style.transform = 'scale(1.1)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'none';
          e.currentTarget.style.boxShadow = '0 5px 15px rgba(0,0,0,0.1)';
          const img = e.currentTarget.querySelector('img');
          if (img) img.style.transform = 'none';
        }}
        className="wow fadeInUp"
        data-wow-delay={position === 'large-left' ? '0.1s' :
          position === 'medium-top' ? '0.2s' :
            position === 'small-1' ? '0.3s' :
              position === 'small-2' ? '0.4s' :
                position === 'medium-bottom' ? '0.5s' : '0.6s'}
      >
        <div style={{
          position: 'relative',
          width: '100%',
          height: '100%',
          overflow: 'hidden'
        }}>
          <img
            src={item.image}
            alt={item.title}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'center',
              transition: 'transform 0.6s ease'
            }}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = `https://via.placeholder.com/800x${config.height.slice(0, -2)}/f8f9fa/6c757d?text=${encodeURIComponent(item.title)}`;
            }}
          />
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.4) 100%)',
            borderRadius: '15px'
          }}></div>
          <div style={{
            position: 'absolute',
            top: '20px',
            left: '20px',
            background: 'linear-gradient(135deg, #ff6b6b, #ff4757)',
            color: 'white',
            padding: '10px 20px',
            borderRadius: '25px',
            fontWeight: 'bold',
            fontSize: '1rem',
            boxShadow: '0 4px 15px rgba(255,107,107,0.4)',
            zIndex: 10
          }}>
            {item.discount}
          </div>
          <div style={{
            position: 'absolute',
            bottom: '20px',
            right: '20px',
            background: 'rgba(255,255,255,0.95)',
            color: '#2d3436',
            padding: '12px 25px',
            borderRadius: '25px',
            fontWeight: 'bold',
            fontSize: '1.1rem',
            boxShadow: '0 5px 20px rgba(0,0,0,0.2)',
            maxWidth: '85%',
            zIndex: 10,
            textAlign: 'center'
          }}>
            {item.title}
          </div>
        </div>
      </div>
    );
  };

  // Get active gallery items
  const activeItems = galleryItems.filter(item => item.isActive);

  // Update grid style on window resize
  const [gridStyle, setGridStyle] = useState(getGridStyle());

  useEffect(() => {
    const handleResize = () => {
      setGridStyle(getGridStyle());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div>
      {/* Hero Section - আগের কোড থেকে নেওয়া */}
      <div className="container-fluid bg-primary py-5 mb-5 hero-header">
        <div className="container py-5">
          <div className="row justify-content-center py-5">
            <div className="col-lg-10 pt-lg-5 mt-lg-5 text-center">
              <h1 className="display-3 text-white animated slideInDown">
                Destination
              </h1>
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb justify-content-center">
                  <li className="breadcrumb-item">
                    <a href="/">Home</a>
                  </li>
                  <li className="breadcrumb-item text-white active" aria-current="page">
                    Destination
                  </li>
                </ol>
              </nav>
            </div>
          </div>
        </div>
      </div>

      {/* Destination Gallery */}
      <div className="container-xxl py-5 destination">
        <div className="container">
          <div className="text-center wow fadeInUp" data-wow-delay="0.1s">
            <h6 className="section-title bg-white text-center text-primary px-3">
              Destination
            </h6>
            <h1 className="mb-5">Popular Destination</h1>
          </div>

          {/* Loading State */}
          {loading ? (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '400px'
            }}>
              <div className="spinner-border text-primary" style={{
                width: '4rem',
                height: '4rem',
                borderWidth: '0.3em'
              }} role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) :

            /* Error State */
            error ? (
              <div style={{
                padding: '40px',
                textAlign: 'center',
                background: 'linear-gradient(135deg, #ffeaa7, #fab1a0)',
                borderRadius: '15px',
                margin: '20px 0'
              }}>
                <i className="fas fa-exclamation-triangle fa-3x text-danger mb-3"></i>
                <h3 className="text-danger mb-3">Oops! Something went wrong</h3>
                <p className="lead mb-4">{error}</p>
                <button
                  className="btn btn-primary btn-lg"
                  onClick={fetchDestinationGallery}
                >
                  <i className="fas fa-redo me-2"></i>
                  Try Again
                </button>
              </div>
            ) :

              /* Empty State */
              activeItems.length === 0 ? (
                <div style={{
                  padding: '60px 20px',
                  textAlign: 'center'
                }}>
                  <div style={{
                    fontSize: '4rem',
                    color: '#b2bec3',
                    marginBottom: '20px'
                  }}>
                    <i className="fas fa-images"></i>
                  </div>
                  <h3 className="text-muted mb-3">No Destinations Available</h3>
                  <p className="text-muted lead mb-4">
                    Our gallery is currently being updated. Please check back soon!
                  </p>
                  <div style={{
                    background: 'linear-gradient(135deg, #74b9ff, #0984e3)',
                    color: 'white',
                    padding: '8px 15px',
                    borderRadius: '20px',
                    fontSize: '0.9rem',
                    boxShadow: '0 3px 10px rgba(116,185,255,0.3)'
                  }} className="d-inline-block">
                    <i className="fas fa-info-circle me-2"></i>
                    Gallery will be updated shortly
                  </div>
                </div>
              ) :

                /* Gallery Grid */
                (
                  <>
                    <div style={gridStyle}>
                      {/* Large Left */}
                      {renderGalleryItem(getItemByPosition('large-left'), 'large-left')}

                      {/* Large Right */}
                      {renderGalleryItem(getItemByPosition('large-right'), 'large-right')}

                      {/* Medium Top */}
                      {renderGalleryItem(getItemByPosition('medium-top'), 'medium-top')}

                      {/* Medium Bottom */}
                      {renderGalleryItem(getItemByPosition('medium-bottom'), 'medium-bottom')}

                      {/* Small 1 */}
                      {renderGalleryItem(getItemByPosition('small-1'), 'small-1')}

                      {/* Small 2 */}
                      {renderGalleryItem(getItemByPosition('small-2'), 'small-2')}
                    </div>

                    {/* Statistics */}
                    <div className="text-center mt-5 pt-4">
                      <div style={{
                        background: 'linear-gradient(135deg, #74b9ff, #0984e3)',
                        color: 'white',
                        padding: '8px 15px',
                        borderRadius: '20px',
                        fontSize: '0.9rem',
                        boxShadow: '0 3px 10px rgba(116,185,255,0.3)'
                      }} className="d-inline-block">
                        <i className="fas fa-map-marker-alt me-2"></i>
                        Showing {activeItems.length} destinations
                      </div>
                      <p className="text-muted mt-3">
                        <small>
                          <i className="fas fa-info-circle me-1"></i>
                          Click on any destination to learn more about it
                        </small>
                      </p>
                    </div>
                  </>
                )}
        </div>
      </div>

      {/* Call to Action */}
      <div className="container-fluid bg-light py-5 mt-5">
        <div className="container text-center">
          <h2 className="mb-4">Ready for Your Next Adventure?</h2>
          <p className="lead text-muted mb-4">
            Browse our packages and start planning your dream vacation today
          </p>
          <div className="d-flex justify-content-center gap-3">
            <a href="/packages" className="btn btn-primary btn-lg">
              <i className="fas fa-suitcase me-2"></i>
              View Packages
            </a>
            <a href="/contact" className="btn btn-outline-primary btn-lg">
              <i className="fas fa-headset me-2"></i>
              Contact Us
            </a>
          </div>
        </div>
      </div>

      {/* Add CSS for animations */}
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .wow.fadeInUp {
          animation-name: fadeInUp;
          animation-duration: 0.8s;
          animation-fill-mode: both;
        }
        
        /* Responsive adjustments */
        @media (max-width: 768px) {
          .display-3 {
            font-size: 2.5rem;
          }
        }
        
        @media (max-width: 576px) {
          .display-3 {
            font-size: 2rem;
          }
          
          .btn-lg {
            padding: 0.5rem 1rem;
            font-size: 1rem;
          }
        }
      `}</style>
    </div>
  );
}

export default Destination;